import { useState } from 'react';
import { clearSmsDefault, listInboxes, setSmsDefault } from '../api/endpoints';
import type { Inbox } from '../api/types';
import { identityLine } from '../lib/channelMeta';
import {
  replaceQuestion,
  senderName,
  smsDefaultStatus,
  smsSenderOptions,
  type SmsSenderOption,
  type SmsSenderType,
} from '../lib/smsDefault';
import { ConfirmDialog } from './ConfirmDialog';
import { ErrorNote, Note } from './ErrorNote';
import { Field } from './Field';
import { SmsDefaultLine } from './SmsDefaultLine';
import { Spinner } from './Spinner';
import './smsDefaultForm.css';

/**
 * The Automated SMS card: which number the dispatch system's automated texts go
 * out from, and — for whoever may change it — a form to pick another one:
 * provider, then number, then Save. It sits above both channel lists
 * (ChannelsHome standalone, SettingsView inside the dashboard), because the
 * console is mounted more than one way and a control on one would be missing
 * from the other.
 *
 * It decides nothing itself. The server owns every rule (services/smsDefault.js)
 * and answers through the inbox list; the form offers only what that list says
 * could be the default, and says what a change replaces before it is sent.
 */

type Choice = SmsSenderType | 'off';

const PROVIDERS: readonly SmsSenderType[] = ['ringcentral', 'twilio'];
const PROVIDER_LABEL: Record<SmsSenderType, string> = { ringcentral: 'RingCentral', twilio: 'Twilio' };

interface Pick {
  choice: Choice;
  inboxId: number | null;
}

/** The first number of a provider that can take the job — what switching to it selects. */
const firstUsable = (options: SmsSenderOption[]) => options.find((one) => one.blocked === null)?.inbox.id ?? null;

export function SmsDefaultForm({
  inboxes,
  canChange,
  onOpenInbox,
  reload,
}: {
  inboxes: Inbox[];
  /** `features.connectChannels`. Unknown capabilities count as yes: the server decides either way. */
  canChange: boolean;
  /** How this mount opens an inbox's settings — a route, or a new tab from the embed. */
  onOpenInbox: (inboxId: number) => void;
  /** Re-read the inbox list, so the line, the badge and this form follow a change. */
  reload: () => void;
}) {
  // Out here rather than in the fields, which remount once the change comes back
  // in the list: "Saved" — or "it changed while you looked" — has to outlive the
  // remount it causes.
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);
  const status = smsDefaultStatus(inboxes);
  if (status.kind === 'none') return null;
  // Read-only: the line alone, exactly as before there was a form.
  if (!canChange) return <SmsDefaultLine inboxes={inboxes} onOpenInbox={onOpenInbox} />;

  const current = status.kind === 'off' ? null : status.inbox;
  return (
    <section className="card card-pad sms-default-card" aria-labelledby="sms-default-title">
      <h2 id="sms-default-title" className="sms-default-title">
        Automated SMS
      </h2>
      <p className="sms-default-sub">
        Reservation confirmations and the other texts the dispatch system sends go out from one number.
      </p>
      <SmsDefaultLine inboxes={inboxes} onOpenInbox={onOpenInbox} />
      {notice && <Note tone={notice.tone}>{notice.text}</Note>}
      <SmsDefaultFields
        key={current ? current.id : 'off'}
        inboxes={inboxes}
        current={current}
        onEdit={() => setNotice(null)}
        onDone={(text) => {
          setNotice({ tone: 'ok', text });
          reload();
        }}
        onStale={(text) => {
          setNotice({ tone: 'warn', text });
          reload();
        }}
      />
    </section>
  );
}

function SmsDefaultFields({
  inboxes,
  current,
  onEdit,
  onDone,
  onStale,
}: {
  inboxes: Inbox[];
  current: Inbox | null;
  onEdit: () => void;
  onDone: (notice: string) => void;
  /** The server's default is no longer the one this page shows. */
  onStale: (notice: string) => void;
}) {
  const options = smsSenderOptions(inboxes);
  const [pick, setPick] = useState<Pick>(() =>
    current && (current.channelType === 'ringcentral' || current.channelType === 'twilio')
      ? { choice: current.channelType, inboxId: current.id }
      : { choice: 'off', inboxId: null },
  );
  const [confirming, setConfirming] = useState(false);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const numbers = pick.choice === 'off' ? [] : options[pick.choice];
  // Only a number that can take the job: a blocked one is shown, never sent.
  const next =
    pick.choice === 'off'
      ? null
      : (numbers.find((one) => one.inbox.id === pick.inboxId && one.blocked === null)?.inbox ?? null);
  const changed = pick.choice === 'off' ? current !== null : next !== null && next.id !== current?.id;

  const edit = (nextPick: Pick) => {
    onEdit();
    setError(null);
    setPick(nextPick);
  };

  const chooseProvider = (choice: Choice) => {
    if (choice === 'off') return edit({ choice, inboxId: null });
    // Back on the provider that sends now: its own number, so nothing reads as changed.
    const stay = current?.channelType === choice ? current.id : null;
    edit({ choice, inboxId: stay ?? firstUsable(options[choice]) });
  };

  /**
   * The confirm names the default the SERVER has, not the one this page loaded
   * with. The standalone Channels page has no socket, so a page left open while
   * somebody else moved the default would ask "replace X?" about a number that
   * no longer has the job — and the answer would replace a number nobody named.
   * So Save asks the server first; a default that moved since is shown, and the
   * choice made again, instead of confirmed against.
   */
  const check = async () => {
    setChecking(true);
    setError(null);
    try {
      const latest = (await listInboxes()).find((inbox) => inbox.defaultSmsSender === true) ?? null;
      if ((latest?.id ?? null) !== (current?.id ?? null)) {
        onStale(
          latest
            ? `The default changed to ${senderName(latest)} while this page was open — check your choice and press Save again.`
            : 'Automated SMS was turned off while this page was open — check your choice and press Save again.',
        );
        return;
      }
      setConfirming(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not check the current default sender.');
    } finally {
      setChecking(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      if (next) {
        await setSmsDefault(next.id);
        onDone(`Saved. Automated SMS is sent from ${senderName(next)}.`);
      } else {
        await clearSmsDefault();
        onDone('Saved. Automated SMS is off.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the default sender.');
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <>
      {error && <ErrorNote error={error} />}
      <div className="sms-default-fields" role="group" aria-label="Default SMS sender">
        <Field label="Type">
          <select
            className="select"
            value={pick.choice}
            disabled={busy}
            onChange={(e) => chooseProvider(e.target.value as Choice)}
          >
            {PROVIDERS.map((provider) => (
              <option key={provider} value={provider} disabled={options[provider].length === 0}>
                {options[provider].length === 0
                  ? `${PROVIDER_LABEL[provider]} — no number connected`
                  : PROVIDER_LABEL[provider]}
              </option>
            ))}
            <option value="off">Off — no automated SMS</option>
          </select>
        </Field>
        {pick.choice !== 'off' && (
          <Field label="Number">
            <select
              className="select"
              value={pick.inboxId === null ? '' : String(pick.inboxId)}
              disabled={busy}
              onChange={(e) => edit({ choice: pick.choice, inboxId: Number(e.target.value) })}
            >
              {pick.inboxId === null && (
                <option value="" disabled>
                  Choose a number
                </option>
              )}
              {numbers.map((one) => (
                <option key={one.inbox.id} value={String(one.inbox.id)} disabled={one.blocked !== null}>
                  {one.blocked ? `${one.label} — ${one.blocked}` : one.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        <div className="sms-default-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!changed || busy || checking}
            onClick={() => void check()}
          >
            {checking && <Spinner />}
            Save
          </button>
        </div>
      </div>
      {confirming && (
        <ConfirmDialog
          title={next ? 'Make this the default sender?' : 'Turn automated SMS off?'}
          body={
            <p>
              {!next
                ? 'Automated SMS will stop until you choose another number.'
                : current
                  ? replaceQuestion(current, identityLine(next))
                  : `Automated SMS will be sent from ${senderName(next)}.`}
            </p>
          }
          confirmLabel={!next ? 'Turn off' : current ? 'Replace' : 'Make default'}
          danger={!next}
          busy={busy}
          onConfirm={() => void apply()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
